import { CSSProperties, useState } from "react";
import ModalSymphony from "elements/ModalSymphony";
import { clsx, makeKey, styx } from "modules/Utils";
import Icon from "elements/Icon";
import Sty from "modules/Sty";
import Colors from "modules/Colors";
import { QRCodeSVG } from 'qrcode.react';

interface WebsiteShareModalProps {
    className?: string;
    style?: CSSProperties;
    isOpen: boolean;
    onClose: () => void;
    websiteUrl: string;
}

const WebsiteShareModal = ({
    className,
    style,
    isOpen,
    onClose,
    websiteUrl,
}: WebsiteShareModalProps) => {
    const SHARE_OPTIONS = [
        {
            name: 'facebook',
            icon: 'facebook',
            title: 'Facebook',
        },
        // {
        //     name: 'instagram',
        //     icon: 'instagram',
        //     title: 'Instagram',
        // },
        {
            name: 'twitter',
            icon: 'twitter',
            title: 'Twitter',
        },
    ]

    const handleDownloadQrCode = () => {
        // Create a canvas element to draw the QR code
        const canvas = document.createElement('canvas');
        const qrCodeSvg = document.querySelector('.qrCode') as SVGElement;

        if (qrCodeSvg) {
            // Convert SVG to a data URL
            const svgData = new XMLSerializer().serializeToString(qrCodeSvg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);

            // Create an image from the SVG
            const img = new Image();
            img.onload = () => {
                // Set canvas dimensions
                canvas.width = img.width;
                canvas.height = img.height;

                // Draw the image on the canvas
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0);

                    // Convert canvas to data URL and trigger download
                    const dataUrl = canvas.toDataURL('image/png');
                    const downloadLink = document.createElement('a');
                    downloadLink.href = dataUrl;
                    downloadLink.download = 'website-qrcode.png';
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);

                    // Clean up
                    URL.revokeObjectURL(svgUrl);
                }
            };

            img.src = svgUrl;
        }
    }

    const renderQrCode = () => {
        return (
            <div className="qrCodeContainer" style={sty.qrCodeContainer}>
                <QRCodeSVG
                    value={websiteUrl}
                    size={200}
                    level="H"
                    className="qrCode"
                    style={sty.qrCode}
                />
            </div>
        )
    }

    const renderQrCodeButton = () => {
        return (
            <div
                className="shareQrContainer"
                style={sty.shareQrContainer}
            >
                <Icon
                    className="shareOptionIcon"
                    icon="qrcode"
                    size={24}
                />
                <div
                    className="shareOptionTitle"
                    style={Sty.text16purple}
                    onClick={() => handleDownloadQrCode()}
                >
                    Download QR Code
                </div>
            </div>
        )
    }

    const handleShare = async (platform: string) => {
        const shareUrl = websiteUrl;
        const shareText = 'Check out my website!';

        switch (platform) {
            // case 'tiktok':
            //     window.open(`https://www.tiktok.com/share?url=${encodeURIComponent(shareUrl)}`, '_blank');
            //     break;
            case 'facebook':
                window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
                break;
            case 'twitter':
                window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
                break;
            // case 'instagram':
            //     await navigator.clipboard.writeText(shareUrl);
            //     break;
        }
    }

    const renderSocialShareOptions = () => {
        return (
            <div className="shareOptionsContainer" style={sty.shareOptionsContainer}>
                {SHARE_OPTIONS.map((option, index) => {
                    const {
                        name,
                        icon,
                    } = option;
                    return (
                        <Icon
                            key={makeKey(name, index)}
                            className="shareOptionIcon"
                            style={sty.shareOptionIcon}
                            icon={icon}
                            size={40}
                            onClick={() => handleShare(name)}
                        />
                    )
                })}
            </div>
        )
    }

    return (
        <>
            <ModalSymphony
                className={clsx('websiteShareModalComponent', className)}
                style={styx(sty.websiteShareModalComponent, style)}
                isOpen={isOpen}
                onClose={onClose}
                title="Share"
                subtitle="Share your site"
                maxWidth={364}
            >
                {renderSocialShareOptions()}
                {renderQrCode()}
                {renderQrCodeButton()}
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    websiteShareModalComponent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
    },
    shareOptionsContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
    },
    shareOptionIcon: {
        borderRadius: 8,
        overflow: 'hidden',
    },
    shareQrContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: Colors.purpleLightest,
        padding: 8,
        borderRadius: 8,
        cursor: 'pointer',
    },
    qrCodeContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        backgroundColor: Colors.white,
        borderRadius: 8,
    },
    qrCode: {
        backgroundColor: Colors.white,
    },
    qrCodeUrl: {
        ...Sty.text14,
        color: Colors.grey,
        wordBreak: 'break-all',
        textAlign: 'center',
    },
}

export default WebsiteShareModal;